import { InputChangeEvent } from '@banno/jha-wc/src/js/utils/types';
import { DiContainer, InjectProperty } from '@jack-henry/frontend-utils/di';
import {
    EntitlementFeatureFlag,
    Feature,
    FeatureFlagService,
} from '@treasury/domain/services/feature-flags';
import { checkmarkIcon, circleCloseIcon } from '@treasury/presentation/assets/icons';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { exists } from '@treasury/utils';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { showOverrides } from './feature-flag-overrides.helpers';

const TagName = 'feature-flag-overrides';

/**
 * Minimum amount of characters needed to begin searching.
 */
const SEARCH_THRESHOLD = 3;

/**
 * Presents a UI for overriding feature flag state locally.
 */
@customElement(TagName)
export class FeatureFlagOverrides extends LitElement {
    public static styles = css`
        table {
            width: 100%;
        }

        th {
            font-weight: bold;
        }

        thead.sticky tr th {
            position: sticky;
            top: 0;
            background-color: var(--container-primary, white);
            z-index: 100;
            border-bottom: 1px solid var(--primary-text-color, black);
        }

        .x-icon svg {
            stroke: red;
        }

        .check-icon svg {
            stroke: green;
        }

        .feature-name {
            font-family: monospace;
        }

        .center {
            text-align: center;
        }

        .right {
            text-align: right;
        }
        .input-container {
            display: flex;
        }
        input {
            flex: 1;
            padding: 5px;
        }
    `;

    @InjectProperty({
        async: true,
        token: FeatureFlagService,
    })
    declare private ffServicePromise: Promise<FeatureFlagService>;

    @state()
    private hideBslFeatures = false;

    @state()
    private searchTerm = '';

    protected render() {
        return html` <div class="input-container">
                <input
                    @input="${(e: InputEvent) => this.onFlagSearch(e)}"
                    placeholder="Type to search for feature flags"
                />
            </div>
            <div style="padding: 10px 0;">
                <span>
                    Hide BSL Features
                    <input
                        type="checkbox"
                        @change="${(e: InputChangeEvent) => this.onHideToggle(e)}"
                    />
                </span>
            </div>
            <table>
                <thead class="sticky">
                    <tr>
                        <th>Feature Name</th>
                        <th class="center">Original State</th>
                        <th class="right">Override</th>
                    </tr>
                </thead>
                <tbody>
                    ${until(this.renderRows(), html`Loading features...`)}
                </tbody>
            </table>`;
    }

    private async onToggle(e: InputChangeEvent) {
        if (!e.target) {
            throw new Error('Cannot toggle checkbox. No target input.');
        }

        const { checked, value } = e.target as HTMLInputElement;

        (await this.ffServicePromise).setOverride(value as Feature, checked);
    }

    private onHideToggle(e: InputChangeEvent) {
        if (!e.target) {
            throw new Error('Cannot toggle checkbox. No target input.');
        }

        const { checked } = e.target as HTMLInputElement;
        this.hideBslFeatures = checked;
    }

    private async getFilteredFeatureFlags() {
        const ffService = await this.ffServicePromise;
        const normalizedTerm = this.searchTerm.trim().toLowerCase();

        return ffService.features.filter(f => {
            if (this.hideBslFeatures && f instanceof EntitlementFeatureFlag) {
                return false;
            }

            if (normalizedTerm.length < SEARCH_THRESHOLD) {
                return true;
            }

            const normalizedFeatureName = f.name.trim().toLowerCase();
            return normalizedFeatureName.includes(normalizedTerm);
        });
    }

    private onFlagSearch(e: InputEvent) {
        this.searchTerm = (e.currentTarget as HTMLInputElement)?.value;
    }

    private renderCheckmark() {
        return html`<span role="img" class="check-icon" icon="check-circle"
            >${checkmarkIcon}</span
        >`;
    }

    private renderCross() {
        return html`<span role="img" class="x-icon" icon="times-circle">${circleCloseIcon}</span>`;
    }

    private async renderRows() {
        const ffService = await this.ffServicePromise;
        const features = await this.getFilteredFeatureFlags();

        if (features.length < 1) {
            return html`<p>No features to show.</p>`;
        }

        return features.map(f => {
            const overrideState = ffService.getOverride(f.name);
            const effectiveEnabled = exists(overrideState) ? overrideState : f.enabled;

            return html`<tr>
                <td>
                    <span class="feature-name">${f.name}</span>
                </td>
                <td class="center">${f.enabled ? this.renderCheckmark() : this.renderCross()}</td>
                <td class="center">
                    <input
                        type="checkbox"
                        .value=${f.name}
                        ?checked=${effectiveEnabled}
                        @change="${(e: InputChangeEvent) => this.onToggle(e)}"
                    />
                </td>
            </tr>`;
        });
    }
}

// provide a way to manually invoke overrides view from JS console for non-dev builds
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).showOverrides = async function () {
    const dialogService = (await DiContainer.getInstance()).get(TmDialogService);
    showOverrides(dialogService);
};
